import { createAsyncThunk } from '@reduxjs/toolkit'
import { createThunkName } from '../../../utils/createThunkname'
import {
  CreatePromotionDto,
  GetAllInsurancePlansQueryParams,
  GetAllInsurancePlansResponse,
  GetAllPromotionCodesResponse,
  GetAllPromotionInsurancePlansResponse,
  GetAllPromotionsQueryParams,
  GetAllPromotionsResponse,
  GetPromotionDealerDetailResponse,
  GetPromotionDetailResponse,
  PromotionCodesRequest,
  PromotionDiscountDetailResponse,
  UpdatePromotionDto,
  UpdatePromotionStatusDto
} from './types'
import { AxiosService } from '../../../services/api'
import { PROMOTION_URLS } from '../../../configs/api'
import { REQUEST_OPTIONS } from '../../../constants/request-options'
import { saveData } from '../../../utils/downloadFile'

const PREFIX = 'promotion'

export const PromotionService = {
  getAllPromotionsAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getAllPromotions'),
    async (queryParams: GetAllPromotionsQueryParams) => {
      try {
        const result = await AxiosService.get<GetAllPromotionsResponse>(PROMOTION_URLS.getAllPromotions, {
          params: queryParams
        })
        return result.data
      } catch (error) {
        throw error
      }
    }
  ),
  exportPromotionsAsyncThunk: createAsyncThunk(createThunkName(PREFIX, 'exportPromotions'), async () => {
    try {
      const result = await AxiosService.get(PROMOTION_URLS.exportPromotions, {
        ...REQUEST_OPTIONS
      })
      saveData(result, result.request.getResponseHeader('Content-Disposition'))
    } catch (error) {
      throw error
    }
  }),
  exportPromoCodesAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'exportPromoCodes'),
    async (promotionId: string) => {
      try {
        const result = await AxiosService.get(PROMOTION_URLS.exportPromoCodes(promotionId), {
          ...REQUEST_OPTIONS
        })
        saveData(result, result.request.getResponseHeader('Content-Disposition'))
      } catch (error) {
        throw error
      }
    }
  ),
  getAllInsurancePlansAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getAllInsurancePlans'),
    async (queryParams: GetAllInsurancePlansQueryParams) => {
      try {
        const result = await AxiosService.get<GetAllInsurancePlansResponse>(PROMOTION_URLS.getAllInsurancePlans, {
          params: queryParams
        })
        return result.data.plans
      } catch (error) {
        throw error
      }
    }
  ),
  createPromotionAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'createPromotion'),
    async (dto: CreatePromotionDto) => {
      try {
        await AxiosService.post(PROMOTION_URLS.createPromotion, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  updatePromotionAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'updatePromotion'),
    async (dto: UpdatePromotionDto) => {
      try {
        await AxiosService.put(PROMOTION_URLS.updatePromotion, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  getPromotionDetailAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getPromotionDetail'),
    async (promotionId: string) => {
      try {
        const result = await AxiosService.get<GetPromotionDetailResponse>(
          PROMOTION_URLS.getPromotionDetail(promotionId)
        )
        return result.data.promotion
      } catch (error) {
        throw error
      }
    }
  ),
  getDiscountDetailAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getDiscountDetail'),
    async (promotionId: string) => {
      try {
        const result = await AxiosService.get<PromotionDiscountDetailResponse>(
          PROMOTION_URLS.getDiscountDetail(promotionId)
        )
        return result.data.promotion
      } catch (error) {
        throw error
      }
    }
  ),
  getAllPromotionInsurancePlansAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getAllPromotionInsurancePlans'),
    async (promotionId: string) => {
      try {
        const result = await AxiosService.get<GetAllPromotionInsurancePlansResponse>(
          PROMOTION_URLS.getAllPromotionInsurancePlans(promotionId)
        )
        return result.data.promotion
      } catch (error) {
        throw error
      }
    }
  ),
  getAllPromotionCodesAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getAllPromotionCodes'),
    async (request: PromotionCodesRequest) => {
      try {
        const result = await AxiosService.get<GetAllPromotionCodesResponse>(
          PROMOTION_URLS.getAllPromotionCodes(request.promotionId),
          {
            params: request.queryParams
          }
        )
        return result.data.promotion
      } catch (error) {
        throw error
      }
    }
  ),
  updatePromotionStatusAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'updatePromotionStatus'),
    async (dto: UpdatePromotionStatusDto) => {
      try {
        await AxiosService.put(PROMOTION_URLS.updatePromotionStatus, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  getPromotionDealerDetailAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getPromotionDealerDetail'),
    async (promotionId: string) => {
      try {
        const result = await AxiosService.get<GetPromotionDealerDetailResponse>(
          PROMOTION_URLS.getPromotionDealerDetail(promotionId)
        )
        return result.data.promotionDealerInfo
      } catch (error) {
        throw error
      }
    }
  )
}
